import React, { useEffect, useState } from 'react';
import { getAssets, getCompanies, createAsset, listInvestorPools, getSuppliers } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Truck, Plus, Search, Filter, Building2, Landmark, Users, Store, AlertTriangle, Archive } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { VehicleDetailDialog } from '../components/VehicleDetailDialog';

const statusColors = {
  available: 'bg-green-100 text-green-800',
  rented: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-red-100 text-red-800',
  idle: 'bg-amber-100 text-amber-800',
};

const ownershipTypeColors = {
  own_cash: 'bg-emerald-100 text-emerald-800',
  own_bank_financed: 'bg-blue-100 text-blue-800',
  own_investor_pool: 'bg-purple-100 text-purple-800',
  supplier_rented: 'bg-orange-100 text-orange-800',
};

const ownershipTypeLabels = {
  own_cash: 'Cash Purchase',
  own_bank_financed: 'Bank Financed',
  own_investor_pool: 'Investor Pool',
  supplier_rented: 'Supplier Rented',
};

const ownershipTypeIcons = {
  own_cash: Building2,
  own_bank_financed: Landmark,
  own_investor_pool: Users,
  supplier_rented: Store,
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const end = new Date(dateStr);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end - today) / (1000 * 60 * 60 * 24));
};

const contractStatus = (asset) => {
  if (!asset || asset.ownership_type !== 'supplier_rented') return null;
  const days = daysUntil(asset.contract_end_date);
  if (days === null) return null;
  if (days < 0) return { level: 'expired', days, label: `Expired ${Math.abs(days)}d ago` };
  if (days <= 30) return { level: 'critical', days, label: `Expires in ${days}d` };
  if (days <= 60) return { level: 'warning', days, label: `Expires in ${days}d` };
  return { level: 'ok', days, label: `Expires in ${days}d` };
};

export const FleetPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [assets, setAssets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [investorPools, setInvestorPools] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [formData, setFormData] = useState({
    company_id: '',
    asset_type: '',
    make: '',
    model: '',
    registration_number: '',
    chassis_number: '',
    year: new Date().getFullYear(),
    purchase_value: '',
    purchase_date: new Date().toISOString().split('T')[0],
    ownership_type: 'own_cash',
    // Bank financing fields
    is_financed: false,
    bank_name: '',
    loan_term_months: '',
    monthly_emi: '',
    down_payment: '',
    interest_rate: '',
    first_emi_date: '',
    // Investor pool fields
    investor_pool_id: '',
    // Supplier rental fields (used when ownership_type === 'supplier_rented')
    supplier_id: '',
    supplier_name: '',
    rental_rate_daily: '',
    rental_rate_monthly: '',
    contract_start_date: '',
    contract_duration_years: '',
    contract_notes: '',
    // Common fields
    insurance_expiry: '',
    registration_expiry: ''
  });

  useEffect(() => {
    loadCompanies();
    loadAssets();
    loadInvestorPools();
    loadSuppliers();
  }, []);

  useEffect(() => {
    loadAssets();
  }, [filterCompany, filterStatus, showArchived]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadInvestorPools = async () => {
    try {
      const response = await listInvestorPools({ status: 'open' });
      setInvestorPools(response.data);
    } catch (error) {
      console.error('Failed to load investor pools:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await getSuppliers();
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadAssets = async () => {
    setLoading(true);
    try {
      const companyId = filterCompany === 'all' ? null : filterCompany;
      const status = filterStatus === 'all' ? null : filterStatus;
      const response = await getAssets(companyId, status, { only_archived: showArchived });
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load fleet');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isRented = formData.ownership_type === 'supplier_rented';
    // For supplier-rented vehicles the operator does not "buy" the vehicle,
    // so purchase_value/date are optional — default them so the backend model
    // (which still requires them) accepts the payload.
    const purchaseValue = formData.purchase_value === '' || formData.purchase_value === null
      ? (isRented ? 0 : NaN)
      : parseFloat(formData.purchase_value);
    if (Number.isNaN(purchaseValue)) {
      toast.error('Purchase value is required');
      return;
    }
    const purchaseDate = formData.purchase_date || new Date().toISOString().split('T')[0];
    if (isRented && !(formData.supplier_id || (formData.supplier_name || '').trim())) {
      toast.error('Please select or type a supplier for a rented vehicle');
      return;
    }
    try {
      const data = {
        ...formData,
        year: parseInt(formData.year),
        purchase_value: purchaseValue,
        purchase_date: purchaseDate,
        // Set is_financed based on ownership type for backward compatibility
        is_financed: formData.ownership_type === 'own_bank_financed',
        loan_term_months: formData.loan_term_months ? parseInt(formData.loan_term_months) : null,
        monthly_emi: formData.monthly_emi ? parseFloat(formData.monthly_emi) : null,
        down_payment: formData.down_payment ? parseFloat(formData.down_payment) : null,
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        // Clear investor pool if not investor pool type
        investor_pool_id: formData.ownership_type === 'own_investor_pool' ? formData.investor_pool_id : null,
        // Supplier fields only when supplier_rented
        supplier_id: isRented ? (formData.supplier_id || null) : null,
        supplier_name: isRented ? ((formData.supplier_name || '').trim() || null) : null,
        rental_rate_daily: isRented && formData.rental_rate_daily ? parseFloat(formData.rental_rate_daily) : null,
        rental_rate_monthly: isRented && formData.rental_rate_monthly ? parseFloat(formData.rental_rate_monthly) : null,
        contract_start_date: isRented ? (formData.contract_start_date || null) : null,
        contract_duration_years: isRented && formData.contract_duration_years ? parseFloat(formData.contract_duration_years) : null,
        contract_notes: isRented ? (formData.contract_notes || null) : null,
      };
      await createAsset(data);
      toast.success('Asset added successfully!');
      setDialogOpen(false);
      loadAssets();
      // Reset form
      setFormData({
        company_id: '',
        asset_type: '',
        make: '',
        model: '',
        registration_number: '',
        chassis_number: '',
        year: new Date().getFullYear(),
        purchase_value: '',
        purchase_date: new Date().toISOString().split('T')[0],
        ownership_type: 'own_cash',
        is_financed: false,
        bank_name: '',
        loan_term_months: '',
        monthly_emi: '',
        down_payment: '',
        interest_rate: '',
        first_emi_date: '',
        investor_pool_id: '',
        supplier_id: '',
        supplier_name: '',
        rental_rate_daily: '',
        rental_rate_monthly: '',
        contract_start_date: '',
        contract_duration_years: '',
        contract_notes: '',
        insurance_expiry: '',
        registration_expiry: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add asset');
    }
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch = asset.registration_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.model.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="fleet-title">
            {showArchived ? 'Fleet Archive' : 'Fleet Management'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {assets.length} {showArchived ? 'archived' : 'active'} asset{assets.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => setShowArchived((v) => !v)}
              data-testid="fleet-archive-toggle"
            >
              <Archive className="h-4 w-4 mr-2" />
              {showArchived ? 'Back to Active Fleet' : 'View Archive'}
            </Button>
          )}
          {!showArchived && isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="add-asset-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Asset
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Asset</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company *</Label>
                  <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })} required>
                    <SelectTrigger data-testid="asset-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Asset Type *</Label>
                  <Input value={formData.asset_type} onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })} required data-testid="asset-type-input" placeholder="Pickup, Excavator, Truck" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Make *</Label>
                  <Input value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} required data-testid="make-input" placeholder="Toyota, Isuzu" />
                </div>
                <div>
                  <Label>Model *</Label>
                  <Input value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} required data-testid="model-input" placeholder="Hilux, D-Max" />
                </div>
                <div>
                  <Label>Year *</Label>
                  <Input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} required data-testid="year-input" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Registration Number *</Label>
                  <Input value={formData.registration_number} onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })} required data-testid="reg-number-input" />
                </div>
                <div>
                  <Label>Chassis Number</Label>
                  <Input value={formData.chassis_number} onChange={(e) => setFormData({ ...formData, chassis_number: e.target.value })} data-testid="chassis-number-input" placeholder="Optional" />
                </div>
                <div>
                  <Label>Purchase Value {formData.ownership_type === 'supplier_rented' ? '' : '*'}</Label>
                  <Input
                    type="number"
                    value={formData.purchase_value}
                    onChange={(e) => setFormData({ ...formData, purchase_value: e.target.value })}
                    required={formData.ownership_type !== 'supplier_rented'}
                    placeholder={formData.ownership_type === 'supplier_rented' ? 'Not applicable — leave blank' : ''}
                    data-testid="purchase-value-input"
                  />
                </div>
                <div>
                  <Label>Purchase Date {formData.ownership_type === 'supplier_rented' ? '' : '*'}</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    required={formData.ownership_type !== 'supplier_rented'}
                    data-testid="purchase-date-input"
                  />
                </div>
              </div>

              {/* Ownership Type Selection */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
                <Label className="text-base font-semibold">Ownership Type *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(ownershipTypeLabels).map(([value, label]) => {
                    const Icon = ownershipTypeIcons[value];
                    const isSelected = formData.ownership_type === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          ownership_type: value,
                          is_financed: value === 'own_bank_financed'
                        })}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-accent bg-accent/10' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`ownership-${value}`}
                      >
                        <Icon className={`h-5 w-5 ${isSelected ? 'text-accent' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-accent' : 'text-gray-700'}`}>
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Bank Financed Fields */}
              {formData.ownership_type === 'own_bank_financed' && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="col-span-2">
                    <Label className="text-blue-800 font-semibold flex items-center gap-2">
                      <Landmark className="h-4 w-4" />
                      Bank Financing Details
                    </Label>
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} data-testid="bank-name-input" />
                  </div>
                  <div>
                    <Label>Loan Term (months)</Label>
                    <Input type="number" value={formData.loan_term_months} onChange={(e) => setFormData({ ...formData, loan_term_months: e.target.value })} data-testid="loan-term-input" />
                  </div>
                  <div>
                    <Label>Monthly EMI</Label>
                    <Input type="number" value={formData.monthly_emi} onChange={(e) => setFormData({ ...formData, monthly_emi: e.target.value })} data-testid="monthly-emi-input" />
                  </div>
                  <div>
                    <Label>Down Payment</Label>
                    <Input type="number" value={formData.down_payment} onChange={(e) => setFormData({ ...formData, down_payment: e.target.value })} data-testid="down-payment-input" />
                  </div>
                  <div>
                    <Label>Interest Rate (%)</Label>
                    <Input type="number" value={formData.interest_rate} onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })} data-testid="interest-rate-input" />
                  </div>
                  <div>
                    <Label>First EMI Date</Label>
                    <Input type="date" value={formData.first_emi_date} onChange={(e) => setFormData({ ...formData, first_emi_date: e.target.value })} data-testid="first-emi-date-input" />
                  </div>
                </div>
              )}

              {/* Investor Pool Fields */}
              {formData.ownership_type === 'own_investor_pool' && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-800 font-semibold flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4" />
                    Investor Pool Assignment
                  </Label>
                  <Select 
                    value={formData.investor_pool_id} 
                    onValueChange={(v) => setFormData({ ...formData, investor_pool_id: v })}
                  >
                    <SelectTrigger data-testid="investor-pool-select">
                      <SelectValue placeholder="Select investor pool" />
                    </SelectTrigger>
                    <SelectContent>
                      {investorPools.length === 0 ? (
                        <SelectItem value="none" disabled>No open pools available</SelectItem>
                      ) : (
                        investorPools.map((pool) => (
                          <SelectItem key={pool.id} value={pool.id}>
                            {pool.pool_name} ({pool.pool_raised_amount?.toLocaleString()} / {pool.pool_target_amount?.toLocaleString()} SAR)
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {investorPools.length === 0 && (
                    <p className="text-xs text-purple-600 mt-2">
                      Create an investor pool first in the Investor Pools module.
                    </p>
                  )}
                </div>
              )}

              {/* Supplier Rented Fields */}
              {formData.ownership_type === 'supplier_rented' && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
                  <Label className="text-orange-800 font-semibold flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Supplier Details
                  </Label>
                  <div>
                    <Label>Supplier <span className="text-xs text-orange-700 font-normal">(type to search or add new)</span></Label>
                    <Input
                      list="fleet-supplier-list"
                      value={formData.supplier_name || ''}
                      onChange={(e) => {
                        const typed = e.target.value;
                        const match = suppliers.find(s => s.name === typed);
                        setFormData({
                          ...formData,
                          supplier_name: typed,
                          supplier_id: match ? match.id : '',
                        });
                      }}
                      placeholder="Start typing a supplier name..."
                      data-testid="fleet-supplier-input"
                    />
                    <datalist id="fleet-supplier-list">
                      {suppliers.map((s) => <option key={s.id} value={s.name} />)}
                    </datalist>
                    {formData.supplier_name && !formData.supplier_id && (
                      <p className="text-xs text-orange-700 mt-1">
                        New supplier &quot;{formData.supplier_name}&quot; — will be saved on this vehicle.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Daily Rate (SAR)</Label>
                      <Input
                        type="number"
                        value={formData.rental_rate_daily || ''}
                        onChange={(e) => setFormData({ ...formData, rental_rate_daily: e.target.value })}
                        data-testid="fleet-daily-rate-input"
                      />
                    </div>
                    <div>
                      <Label>Monthly Rate (SAR)</Label>
                      <Input
                        type="number"
                        value={formData.rental_rate_monthly || ''}
                        onChange={(e) => setFormData({ ...formData, rental_rate_monthly: e.target.value })}
                        data-testid="fleet-monthly-rate-input"
                      />
                    </div>
                  </div>

                  {/* Contract Duration */}
                  <div className="pt-3 mt-2 border-t border-orange-200">
                    <Label className="text-orange-800 font-semibold text-sm">Contract Period</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label>Contract Start Date</Label>
                        <Input
                          type="date"
                          value={formData.contract_start_date || ''}
                          onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                          data-testid="fleet-contract-start-input"
                        />
                      </div>
                      <div>
                        <Label>Duration (Years)</Label>
                        <Input
                          type="number"
                          step="0.25"
                          min="0"
                          placeholder="e.g. 1, 1.5, 2"
                          value={formData.contract_duration_years || ''}
                          onChange={(e) => setFormData({ ...formData, contract_duration_years: e.target.value })}
                          data-testid="fleet-contract-duration-input"
                        />
                      </div>
                    </div>
                    {formData.contract_start_date && formData.contract_duration_years && (
                      <p className="text-xs text-orange-700 mt-2" data-testid="fleet-contract-end-preview">
                        Contract will end on:{' '}
                        <strong>
                          {(() => {
                            const start = new Date(formData.contract_start_date);
                            const days = Math.round(parseFloat(formData.contract_duration_years) * 365.25);
                            const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
                            return end.toISOString().slice(0, 10);
                          })()}
                        </strong>
                      </p>
                    )}
                    <div className="mt-3">
                      <Label>Contract Notes</Label>
                      <Input
                        value={formData.contract_notes || ''}
                        onChange={(e) => setFormData({ ...formData, contract_notes: e.target.value })}
                        placeholder="Renewal clause, penalty terms, etc."
                        data-testid="fleet-contract-notes-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">Cancel</Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-asset-button">Add Asset</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by registration, make, or model"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-[200px]" data-testid="filter-company-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="rented">Rented</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="idle">Idle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading fleet...</div>
        </div>
      ) : filteredAssets.length === 0 ? (
        <Card className="p-12 text-center">
          <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No assets found</h3>
          <p className="text-muted-foreground mb-4">Add your first asset to get started</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => {
            const ownershipType = asset.ownership_type || (asset.is_financed ? 'own_bank_financed' : 'own_cash');
            const OwnershipIcon = ownershipTypeIcons[ownershipType] || Building2;
            const contract = contractStatus(asset);
            const cardBorder = contract?.level === 'expired'
              ? 'ring-2 ring-red-400 border-red-300'
              : contract?.level === 'critical'
                ? 'ring-2 ring-orange-400 border-orange-300'
                : contract?.level === 'warning'
                  ? 'border-amber-300'
                  : '';
            return (
            <Card
              key={asset.id}
              className={`p-6 hover:shadow-lg transition-shadow duration-200 cursor-pointer ${cardBorder}`}
              onClick={() => { setSelectedAsset(asset); setDetailOpen(true); }}
              data-testid={`asset-card-${asset.id}`}
            >
              {contract && (contract.level === 'expired' || contract.level === 'critical') && (
                <div
                  className={`mb-3 flex items-center gap-2 text-xs font-semibold px-2 py-1.5 rounded ${
                    contract.level === 'expired'
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : 'bg-orange-100 text-orange-800 border border-orange-200'
                  }`}
                  data-testid={`contract-alert-${asset.id}`}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>{contract.level === 'expired' ? 'Contract Expired' : 'Contract Expiring Soon'} · {contract.label}</span>
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Truck className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold" data-testid={`asset-name-${asset.id}`}>{asset.make} {asset.model}</h3>
                    <p className="text-sm text-muted-foreground">{asset.asset_type}</p>
                  </div>
                </div>
                <Badge className={statusColors[asset.status]} data-testid={`asset-status-${asset.id}`}>
                  {asset.status}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reg No:</span>
                  <span className="font-mono font-semibold">{asset.registration_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Year:</span>
                  <span>{asset.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Value:</span>
                  <span className="font-semibold">{asset.purchase_value.toLocaleString()} SAR</span>
                </div>
                {/* Ownership Type Badge */}
                <div className="flex justify-between items-center pt-2 mt-2 border-t">
                  <span className="text-muted-foreground">Ownership:</span>
                  <Badge className={ownershipTypeColors[ownershipType] || 'bg-gray-100'}>
                    <OwnershipIcon className="h-3 w-3 mr-1" />
                    {ownershipTypeLabels[ownershipType] || 'Unknown'}
                  </Badge>
                </div>
                {/* Bank Finance Details */}
                {ownershipType === 'own_bank_financed' && asset.monthly_emi && (
                  <div className="pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Monthly EMI:</span>
                      <span className="text-sm font-semibold text-blue-600">{asset.monthly_emi?.toLocaleString()} SAR</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{asset.bank_name}</p>
                  </div>
                )}
                {/* Investor Pool Details */}
                {ownershipType === 'own_investor_pool' && asset.investor_pool_id && (
                  <div className="pt-2">
                    <div className="flex items-center gap-1 text-xs text-purple-600">
                      <Users className="h-3 w-3" />
                      <span>Pool: {asset.investor_pool_id}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )})}
        </div>
      )}

      {/* Vehicle Detail with Maintenance & Earnings tabs */}
      <VehicleDetailDialog
        asset={selectedAsset}
        open={detailOpen}
        onOpenChange={(o) => { setDetailOpen(o); if (!o) setSelectedAsset(null); }}
        onAssetChanged={loadAssets}
      />
    </div>
  );
};
